/**
 * Conversational Onboarding Service
 * ----------------------------------
 * API calls for the chat-based onboarding flow.
 * Uses dynamic slot-filling and multi-turn context.
 */

import { api, type ApiResponse } from '../utils';

// Types for conversational onboarding
export interface ConversationalStartRequest {
  objective?: string;
  /** White-label organizer/brand name so the greeting reads "Linc from <Organizer>". */
  organizer_name?: string;
  /**
   * Skip the saved-slot resume and start cold. Set when an already-onboarded user
   * redoes onboarding for a new event with a different goal: resuming hydrates
   * their old answers, lands them at ~100% and never asks what changed, so the
   * re-run captures nothing.
   */
  force_new?: boolean;
  /** Event the re-run is for, so the opener names it instead of the stale brand. */
  event_name?: string;
}

export interface ConversationalChatRequest {
  message: string;
  session_id?: string;
}

export interface ConversationalCompleteRequest {
  session_id: string;
  /**
   * Apr-23 Fix #4 (Apr-22): optional slot_overrides carry user
   * edits from the review screen through to persona generation. Keys are
   * slot names (e.g. `industry_focus`); values replace the extracted value.
   * Only passed when the user edits at least one field on the review step.
   * Mobile ships this wiring today; web frontend type kept in parity for
   * when the same edit UI lands here.
   */
  slot_overrides?: Record<string, string>;
  /**
   * Jun-20: the white-label subdomain the attendee is on (window.location.hostname).
   * The backend uses it to auto-enrol the attendee into that event for event-scoped
   * matching. Sent always; the backend acts only on a provisioned organiser subdomain
   * (the main domain resolves to no event). Passed in the body because CloudFront does
   * not forward the Origin header to the origin (same reason branding uses ?host=).
   */
  host?: string;
}

export interface ExtractedSlot {
  value: any;
  confidence: number;
  status?: string;
}

export interface ConversationalStartResponse {
  session_id: string;
  greeting: string;
  suggested_questions: string[];
  progress_percent: number;
  resumed?: boolean;
  // Prior conversation turns to replay when a returning user resumes onboarding.
  history?: { role: 'user' | 'assistant'; content: string }[];
}

export interface ConversationalChatResponse {
  session_id: string;
  ai_response: string;
  extracted_slots: Record<string, ExtractedSlot>;
  all_slots: Record<string, ExtractedSlot>;
  completion_percent: number;
  next_questions: string[];
  phase: string;
  is_complete: boolean;
}

export interface ConversationalProgressResponse {
  session_id: string;
  user_id: string;
  phase: string;
  progress_percent: number;
  slots_filled: number;
  total_required: number;
  estimated_remaining_minutes: number;
  is_complete: boolean;
}

export interface ConversationalFinalizeResponse {
  session_id: string;
  user_id: string;
  collected_data: Record<string, any>;
  turn_count: number;
  completed_at: string;
}

export interface ConversationalCompleteResponse {
  success: boolean;
  user_id: string;
  message: string;
  profile_created: boolean;
  persona_task_id?: string;
}

/**
 * Start a new conversational onboarding session
 */
export const startConversationalOnboarding = async (
  data?: ConversationalStartRequest
): Promise<ApiResponse<ConversationalStartResponse>> => {
  try {
    const response = await api.post('/onboarding/conversational/start', data || {});
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to start conversational onboarding',
      result: null as any,
    };
  }
};

/**
 * Upload a resume/CV during onboarding.
 * QA fix (Jun-29, event review): routes through the BACKEND (authed; saves to S3 +
 * user_documents AND forwards to the AI service for persona generation), replacing the
 * old direct browser->AI-service fetch that fell back to localhost in the prod build
 * and bypassed auth/CORS. That is the "paperclip does nothing" bug Gintaras hit (and
 * why many users pasted whole CVs into the chat instead).
 */
export const uploadOnboardingResume = async (
  file: File,
  sessionId?: string
): Promise<ApiResponse<unknown>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (sessionId) formData.append('session_id', sessionId);
    const response = await api.post('/onboarding/upload-resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to upload resume',
      result: null as any,
    };
  }
};

/**
 * Send a chat message in the onboarding session
 */
export const sendOnboardingMessage = async (
  data: ConversationalChatRequest
): Promise<ApiResponse<ConversationalChatResponse>> => {
  try {
    const response = await api.post('/onboarding/conversational/chat', data);
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to send message',
      result: null as any,
    };
  }
};

/**
 * Get progress for an onboarding session
 */
export const getConversationalProgress = async (
  sessionId: string
): Promise<ApiResponse<ConversationalProgressResponse>> => {
  try {
    const response = await api.get(`/onboarding/conversational/progress/${sessionId}`);
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to get progress',
      result: null as any,
    };
  }
};

/**
 * Finalize the onboarding session
 */
export const finalizeConversationalOnboarding = async (
  sessionId: string
): Promise<ApiResponse<ConversationalFinalizeResponse>> => {
  try {
    const response = await api.post(`/onboarding/conversational/finalize/${sessionId}`);
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to finalize onboarding',
      result: null as any,
    };
  }
};

/**
 * Complete onboarding and create profile/persona
 */
export const completeConversationalOnboarding = async (
  data: ConversationalCompleteRequest
): Promise<ApiResponse<ConversationalCompleteResponse>> => {
  try {
    const response = await api.post('/onboarding/conversational/complete', data);
    return response.data;
  } catch (error: any) {
    return {
      code: error?.response?.status || 500,
      message: error?.response?.data?.message || 'Failed to complete onboarding',
      result: null as any,
    };
  }
};
